// S4 (v0.15.0) — Change password with live strength indicator.
import { useMemo, useState } from "react";
import { endSession } from "@/store/session";
import { useNavigate } from "react-router-dom";
import { CheckIcon, XIcon } from "lucide-react";

import { useChangePassword, PASSWORD_RULES } from "@/api/s4";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";

export default function ChangePasswordPage() {
  const change = useChangePassword();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const rules = useMemo(
    () => PASSWORD_RULES.map((r) => ({ ...r, passed: r.test(next) })),
    [next],
  );
  const allPass = rules.every((r) => r.passed);
  const matches = next.length > 0 && next === confirm;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!matches) {
      setError("New passwords do not match.");
      return;
    }
    try {
      await change.mutateAsync({
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });
      setSuccess(true);
      // Backend revokes other sessions; current session may also be
      // invalidated by some backends — redirect to /login after 3s to
      // be safe.
      setTimeout(() => {
        endSession();
        nav("/login", { replace: true });
      }, 3000);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("current")) {
        setError("Current password is incorrect.");
      } else {
        setError(typeof detail === "string" ? detail : "Failed to change password");
      }
    }
  }

  if (success) {
    return (
      <div className="mx-auto max-w-md">
        <Card className="border-emerald-500/30">
          <CardContent className="space-y-2 p-6 text-center">
            <p className="text-lg font-semibold text-emerald-700">
              Password changed successfully.
            </p>
            <p className="text-sm text-muted-foreground">
              You have been signed out of all other devices for security.
              Redirecting to login…
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Change Password
        </h1>
        <p className="text-sm text-muted-foreground">
          You'll be signed out of all other devices after a successful change.
        </p>
      </div>
      <Card>
        <CardContent className="p-4">
          <form className="space-y-3" onSubmit={handleSubmit}>
            <div>
              <Label>Current Password</Label>
              <PasswordInput
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <div>
              <Label>New Password</Label>
              <PasswordInput
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <PasswordInput
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
              {confirm.length > 0 && !matches && (
                <p className="mt-1 text-xs text-red-600">
                  Passwords do not match
                </p>
              )}
            </div>

            <div className="rounded border border-border bg-muted/20 p-3">
              <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                Password requirements
              </p>
              <ul className="space-y-1 text-xs">
                {rules.map((r) => (
                  <li
                    key={r.key}
                    className={`flex items-center gap-2 ${
                      r.passed ? "text-emerald-600" : "text-muted-foreground"
                    }`}
                  >
                    {r.passed ? (
                      <CheckIcon className="h-3 w-3" />
                    ) : (
                      <XIcon className="h-3 w-3 text-red-500" />
                    )}
                    {r.label}
                  </li>
                ))}
              </ul>
            </div>

            {error && (
              <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
                {error}
              </p>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={!allPass || !matches || !current || change.isPending}
            >
              {change.isPending ? "Saving…" : "Change Password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
